import { useState } from "react";
import { useOutletContext } from "react-router-dom";
import { MapPin, Phone, Mail, Clock, Send } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import api, { apiError } from "@/lib/api";
import { PageHero, Reveal } from "@/components/public/Shared";

export function toMapEmbedUrl(url) {
  if (!url) return "";
  if (url.includes("output=embed") || url.includes("/maps/embed")) return url;
  const at = url.match(/!3d(-?\d+\.\d+)!4d(-?\d+\.\d+)/) || url.match(/@(-?\d+\.\d+),(-?\d+\.\d+)/);
  if (at) {
    return `https://maps.google.com/maps?q=${at[1]},${at[2]}&z=17&hl=en&output=embed`;
  }
  return `https://maps.google.com/maps?q=${encodeURIComponent("Christ Church CNI Rajpura Punjab")}&z=16&hl=en&output=embed`;
}

export default function Contact() {
  const { settings } = useOutletContext() || {};
  const s = settings || {};
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/public/contact", form);
      toast.success("Thank you! Your message has been sent.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error(apiError(err, "Could not send your message. Please try again."));
    } finally {
      setSending(false);
    }
  };

  const cards = [
    { icon: MapPin, label: "Address", value: s.address || "[Placeholder] Church address, Rajpura" },
    { icon: Phone, label: "Phone", value: s.phone || "[Placeholder] +91-XXXXX-XXXXX" },
    { icon: Mail, label: "Email", value: s.email || "[Placeholder] church@example.com" },
    { icon: Clock, label: "Worship", value: s.service_note || "[Placeholder] Timings to be announced" },
  ];

  return (
    <div data-testid="contact-page">
      <PageHero title="Contact Us" subtitle="We would love to hear from you. Reach out with any questions or prayer requests." />
      <div className="mx-auto max-w-6xl px-5 py-16 sm:px-8 sm:py-24">
        <div className="grid gap-12 lg:grid-cols-2">
          <Reveal>
            <div className="space-y-4">
              {cards.map((c) => (
                <div key={c.label} className="flex items-start gap-4 rounded-lg border border-border bg-white p-6 shadow-sm" data-testid={`contact-${c.label.toLowerCase()}`}>
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-navy text-gold">
                    <c.icon className="h-5 w-5" />
                  </span>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">{c.label}</p>
                    <p className="mt-1 text-sm font-medium text-navy">{c.value}</p>
                  </div>
                </div>
              ))}
              <div className="flex h-56 items-center justify-center rounded-lg border border-dashed border-border bg-secondary/50" data-testid="contact-map-placeholder">
                {s.map_embed_url ? (
                  <iframe title="Church location map" src={toMapEmbedUrl(s.map_embed_url)} className="h-full w-full rounded-lg" loading="lazy" referrerPolicy="no-referrer-when-downgrade" allowFullScreen />
                ) : (
                  <p className="flex items-center gap-2 text-sm text-muted-foreground">
                    <MapPin className="h-4 w-4 text-gold-dark" /> [Placeholder] Map location will be embedded here
                  </p>
                )}
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <form onSubmit={submit} className="rounded-lg border border-border bg-white p-8 shadow-sm" data-testid="contact-form">
              <h2 className="font-heading text-2xl font-bold text-navy">Send Us a Message</h2>
              <p className="mt-2 text-sm text-muted-foreground">Fields marked * are required.</p>
              <div className="mt-6 space-y-5">
                <div>
                  <Label htmlFor="contact-name">Your Name *</Label>
                  <Input id="contact-name" data-testid="contact-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" />
                </div>
                <div>
                  <Label htmlFor="contact-email">Email (optional)</Label>
                  <Input id="contact-email" data-testid="contact-email-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5" />
                </div>
                <div>
                  <Label htmlFor="contact-phone">Phone Number *</Label>
                  <Input id="contact-phone" data-testid="contact-phone-input" type="tel" required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1.5" />
                </div>
                <div>
                  <Label htmlFor="contact-message">Message *</Label>
                  <Textarea id="contact-message" data-testid="contact-message-input" required rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="mt-1.5" />
                </div>
                <Button type="submit" disabled={sending} className="w-full bg-navy text-cream hover:bg-navy-light" data-testid="contact-submit-btn">
                  {sending ? "Sending…" : (<>Send Message <Send className="ml-2 h-4 w-4" /></>)}
                </Button>
              </div>
            </form>
          </Reveal>
        </div>
      </div>
    </div>
  );
}
